import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, MapPin, Crown, Wine, Activity, ChevronDown, Sparkles, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/zk7tuj6l_IMG_1952.jpg";

const WEEKLY = [
  { day: "Mon", titleKey: "week.mon.title", genresKey: "week.mon.genres", subKey: "week.mon.sub", c1: "#FF1B8D", c2: "#FF6BC6" },
  { day: "Tue", titleKey: "week.tue.title", genresKey: "week.tue.genres", subKey: "week.tue.sub", c1: "#FFC857", c2: "#FF6BC6" },
  { day: "Wed", titleKey: "week.wed.title", genresKey: "week.wed.genres", subKey: "week.wed.sub", c1: "#A655F2", c2: "#FF1B8D" },
  { day: "Thu", titleKey: "week.thu.title", genresKey: "week.thu.genres", subKey: "week.thu.sub", c1: "#FF6BC6", c2: "#FFC857" },
  { day: "Fri", titleKey: "week.fri.title", genresKey: "week.fri.genres", subKey: "week.fri.sub", c1: "#5BC0FF", c2: "#A655F2" },
  { day: "Sat", titleKey: "week.sat.title", genresKey: "week.sat.genres", subKey: "week.sat.sub", c1: "#FF1B8D", c2: "#FFC857", featured: true },
  { day: "Sun", titleKey: "week.sun.title", genresKey: "week.sun.genres", subKey: "week.sun.sub", c1: "#FFC857", c2: "#FF1B8D" },
];

/** Open status — 11:00 to 04:00 (next day). */
function useStatus(t) {
  const [s, setS] = useState({ live: false, label: "…" });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const h = now.getHours();
      const live = h >= 11 || h < 4;
      if (live) {
        setS({ live: true, label: t("door.openNow") });
      } else {
        // Next opening date in "BACK <DAY> 11AM" style
        const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
        const next = new Date(now);
        if (h >= 4 && h < 11) {
          // same day re-open at 11:00 if it's an open day; show TODAY
          setS({ live: false, label: `${t("door.closed")} ${days[next.getDay()]} 11AM` });
        } else {
          next.setDate(next.getDate() + 1);
          setS({ live: false, label: `${t("door.closed")} ${days[next.getDay()]} 11AM` });
        }
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [t]);
  return s;
}

export default function Home() {
  const { t, lang } = useLang();
  const [tables, setTables] = useState([]);
  const status = useStatus(t);

  useEffect(() => {
    api.get("/tables").then((r) => setTables(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      {/* ──────────────── HERO ──────────────── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: "100svh" }}>
        {/* SINGLE SEAMLESS BACKGROUND — covers the entire viewport, edge-to-edge, behind every other layer.
            Hardcoded 1054×1492 intrinsic dimensions eliminate CLS. */}
        <img
          fetchPriority="high"
          loading="eager"
          decoding="sync"
          width="1054"
          height="1492"
          src="/hero/hero-bg.jpg"
          alt=""
          data-testid="hero-bg"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            objectFit: "cover",
            objectPosition: "center",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2,
            pointerEvents: "none",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)",
          }}
        />

        {/* Hero content centered — above the background */}
        <div
          className="relative flex flex-col items-center justify-center text-center px-4 pt-20 sm:pt-24 pb-6 sm:pb-8"
          style={{ zIndex: 5, minHeight: "100svh" }}
        >
          {/* Status pill — outlined */}
          <div
            data-testid="door-status"
            className="inline-flex items-center gap-2.5 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full backdrop-blur mb-4 sm:mb-6"
            style={{
              background: "rgba(0,0,0,0.65)",
              border: "1.5px solid #FF1B8D",
              boxShadow: "0 0 18px rgba(255,27,141,0.45)",
            }}
          >
            <span className={"w-2 h-2 rounded-full " + (status.live ? "bg-neon animate-pulse" : "bg-neon")} />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.32em] font-bold text-white">
              {status.label}
            </span>
          </div>

          {/* SVG chroma-key filter — drops dark JPEG background, keeps bright text */}
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <filter id="soho-chroma" colorInterpolationFilters="sRGB">
                <feColorMatrix
                  type="matrix"
                  values="1 0 0 0 0
                          0 1 0 0 0
                          0 0 1 0 0
                          1 1 1 0 -0.55"
                />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="25" intercept="-8" />
                </feComponentTransfer>
              </filter>
            </defs>
          </svg>

          {/* SOHO69 hero wordmark — client-supplied PNG-style asset. Wrapped
              in <h1> so this counts as the page's main heading for SEO while
              staying purely visual for guests. */}
          <h1 className="m-0" data-testid="hero-title-heading">
            <span className="sr-only">Soho69 Tenerife · VIP Lounge &amp; Nightclub</span>
            {/* LCP element — plain JPG, eager, hardcoded intrinsic size. */}
            <img
              fetchPriority="high"
              loading="eager"
              decoding="sync"
              width="1400"
              height="1038"
              src="/hero/hero-wordmark.jpg"
              alt="Soho69"
              data-testid="hero-title"
              draggable={false}
              className="block mx-auto w-full max-w-[92vw] md:max-w-[1100px] h-auto select-none"
              style={{
                filter:
                  "url(#soho-chroma) saturate(1.2) contrast(1.08) drop-shadow(0 0 14px rgba(255,255,255,0.25)) drop-shadow(0 8px 32px rgba(255,27,141,0.6)) drop-shadow(0 0 80px rgba(255,27,141,0.45))",
              }}
            />
          </h1>

          <div
            className="mt-3 md:mt-4 text-xs sm:text-sm md:text-base uppercase tracking-[0.32em] md:tracking-[0.45em] text-neon font-bold"
            style={{ textShadow: "0 0 12px rgba(255,27,141,0.7), 0 2px 6px rgba(0,0,0,0.8)" }}
          >
            {t("hero.overline")}
          </div>

          <p
            className="mt-3 sm:mt-5 md:mt-7 max-w-md md:max-w-2xl text-[13px] sm:text-sm md:text-base text-white/90 leading-relaxed px-2"
            style={{ textWrap: "balance" }}
          >
            {t("hero.tagline")}
          </p>
          <p
            className="mt-1.5 sm:mt-2 max-w-md md:max-w-2xl text-[13px] sm:text-sm md:text-base text-neon font-semibold px-2"
            style={{ textWrap: "balance" }}
          >
            {t("hero.subtag")}
          </p>

          {/* Solid pink RESERVE + BUY A TICKET dual CTAs */}
          <div className="mt-5 sm:mt-7 md:mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3">
            <Link
              to="/book?type=barorder"
              data-testid="hero-reserve-cta"
              className="inline-flex items-center justify-center gap-3 px-7 sm:px-8 md:px-12 py-3 sm:py-4 rounded-full font-bold text-black uppercase tracking-[0.22em] text-sm md:text-base transition-all"
              style={{
                background: "linear-gradient(135deg,#FF1B8D 0%,#FF6BC6 50%,#FF1B8D 100%)",
                boxShadow:
                  "0 0 32px rgba(255,27,141,0.7), 0 0 70px rgba(255,27,141,0.45), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
            >
              <ArrowUpRight size={18} /> {t("hero.cta.reserve")}
            </Link>
            <Link
              to="/drinks-tickets"
              data-testid="hero-ticket-cta"
              className="inline-flex items-center justify-center gap-3 px-7 sm:px-8 md:px-12 py-3 sm:py-4 rounded-full font-bold uppercase tracking-[0.22em] text-sm md:text-base transition-all text-white"
              style={{
                background: "rgba(0,0,0,0.55)",
                border: "2px solid #FF1B8D",
                boxShadow:
                  "0 0 22px rgba(255,27,141,0.35), inset 0 0 22px rgba(255,27,141,0.15)",
              }}
            >
              <Sparkles size={18} className="text-neon" /> Buy a Ticket
            </Link>
            <div className="sm:basis-full flex flex-col sm:items-center">
            <Link
              to="/menu"
              data-testid="hero-menu-cta"
              className="inline-flex items-center justify-center gap-3 px-7 sm:px-8 md:px-12 py-3 sm:py-4 rounded-full font-bold uppercase tracking-[0.22em] text-sm md:text-base transition-all text-white"
              style={{
                background: "rgba(0,0,0,0.55)",
                border: "2px solid #FF1B8D",
                boxShadow:
                  "0 0 22px rgba(255,27,141,0.35), inset 0 0 22px rgba(255,27,141,0.15)",
              }}
            >
              <Wine size={18} className="text-neon" /> {lang === "es" ? "Menú" : "Menu"}
            </Link>
            </div>
          </div>

          {/* SH wreath logo bottom-center */}
          <div className="mt-5 sm:mt-8 md:mt-14 w-full flex justify-center">
            <span
              className="block w-16 h-16 sm:w-20 sm:h-20 md:w-32 md:h-32 rounded-full overflow-hidden border border-neon/60"
              style={{ boxShadow: "0 0 30px rgba(255,27,141,0.55), 0 0 60px rgba(255,27,141,0.3)" }}
            >
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="Soho69 SH" className="w-full h-full object-cover" />
            </span>
          </div>
        </div>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40" style={{ zIndex: 5 }}>
          <ChevronDown size={18} className="animate-bounce" />
        </div>
      </section>

      {/* ──────────────── SCROLLING TICKER ──────────────── */}
      <div className="relative w-full overflow-hidden bg-black border-y border-neon/20 py-4">
        <div className="flex w-max animate-marquee gap-10 px-6 items-center">
          {Array(2).fill(0).map((_, dup) =>
            [
              {
                key: "ticker.drinks",
                image:
                  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/x7b5d1n0_B58A2135-4280-40BA-A51B-073205FAFD33.png",
                imgClass: "h-14 md:h-20",
              },
              { icon: Activity, key: "ticker.sound" },
              { icon: Crown, key: "ticker.vip" },
              {
                key: "ticker.shisha",
                image:
                  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/o4o39ixw_B933F940-1F24-49F0-B247-865C96B2C1F9.png",
                imgClass: "h-10 md:h-14",
              },
            ].map((it, i) => (
              <span key={`${dup}-${i}`} className="flex items-center gap-3 shrink-0">
                {it.icon && <it.icon size={20} className="text-neon" />}
                {it.image && (
                  <img loading="lazy" decoding="async"                     src={it.image}
                    alt=""
                    draggable={false}
                    data-testid={`ticker-img-left-${it.key}`}
                    className={`${it.imgClass || "h-10 md:h-14"} w-auto select-none object-contain`}
                    style={{
                      filter:
                        "url(#soho-chroma) drop-shadow(0 0 10px rgba(255,27,141,0.55)) drop-shadow(0 0 24px rgba(255,27,141,0.35))",
                    }}
                  />
                )}
                <span className="font-heading text-xl md:text-3xl uppercase tracking-wide text-white">
                  {t(it.key)}
                </span>
                {it.image && (
                  <img loading="lazy" decoding="async"                     src={it.image}
                    alt=""
                    draggable={false}
                    data-testid={`ticker-img-right-${it.key}`}
                    className={`${it.imgClass || "h-10 md:h-14"} w-auto select-none object-contain`}
                    style={{
                      filter:
                        "url(#soho-chroma) drop-shadow(0 0 10px rgba(255,27,141,0.55)) drop-shadow(0 0 24px rgba(255,27,141,0.35))",
                    }}
                  />
                )}
                <span className="text-neon text-2xl">·</span>
              </span>
            ))
          )}
        </div>
      </div>

      {/* ──────────────── BLOCK 1 — DRINKS PACKAGES ──────────────── */}
      <section className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 py-16 md:py-24">
        <div className="text-center mb-10 md:mb-14">
          <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{t("packages.kicker")}</div>
          <img loading="lazy" decoding="async"             src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/q7hmdtyn_FB15AFFD-AC26-41BE-9FBE-6EEA8E67465A.png"
            alt="Pick your package"
            data-testid="home-pick-your-package-title"
            draggable={false}
            className="block mx-auto mt-3 w-full max-w-[520px] md:max-w-[680px] h-auto select-none"
            style={{
              filter:
                "url(#soho-chroma) saturate(1.15) contrast(1.05) drop-shadow(0 0 18px rgba(255,27,141,0.55)) drop-shadow(0 0 50px rgba(255,27,141,0.4))",
            }}
          />
          <p className="text-white/65 mt-4 max-w-xl mx-auto text-sm md:text-base">{t("packages.sub")}</p>
        </div>

        <div className="flex flex-col gap-4 md:gap-5">
          {tables.map((tt) => {
            const featured = tt.id === "ciroc";
            const tierKey =
              tt.id === "smirnoff"
                ? "book.tier.standard"
                : tt.id === "ciroc"
                ? "book.tier.premium"
                : "book.tier.exclusive";
            const bottleSrc =
              tt.id === "smirnoff"
                ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/v2xnjob5_2251D45F-704E-4AD3-8985-41807EF1B1F5.png"
                : tt.id === "ciroc"
                ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/x7b5d1n0_B58A2135-4280-40BA-A51B-073205FAFD33.png"
                : "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/6m3o2whn_012B99D8-A70B-4BEE-9339-CDF5B9254A7A.png";
            return (
              <Link
                key={tt.id}
                to="/book?type=barorder"
                data-testid={`package-card-${tt.id}`}
                aria-label={`Reserve ${tt.name} · €${tt.price_eur}`}
                className="relative rounded-3xl group transition block hover:scale-[1.01] focus:outline-none focus-visible:ring-2 focus-visible:ring-neon cursor-pointer"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,27,141,0.08) 0%, rgba(0,0,0,0.55) 60%, rgba(255,27,141,0.08) 100%)",
                  border: `1px solid ${featured ? "rgba(255,27,141,0.7)" : "rgba(255,27,141,0.3)"}`,
                  boxShadow: featured
                    ? "0 0 24px rgba(255,27,141,0.35), inset 0 0 24px rgba(255,27,141,0.12)"
                    : "inset 0 0 18px rgba(255,27,141,0.08)",
                }}
              >
                <div className="flex items-stretch min-h-[150px] md:min-h-[170px]">
                  {/* LEFT — tier + name + small price + capacity */}
                  <div className="flex-1 p-5 md:p-7 flex flex-col justify-center">
                    <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.36em] text-neon">
                      {t(tierKey)}
                    </div>
                    <div className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mt-1.5 md:mt-2 leading-[0.95]">
                      {tt.name.toUpperCase()}
                    </div>
                    <div className="mt-2 md:mt-3 flex items-baseline gap-1 text-neon">
                      <span className="font-display italic text-base md:text-lg" style={{ color: "#FF6BC6" }}>€</span>
                      <span className="font-heading text-xl md:text-2xl" style={{ color: "#FF6BC6" }}>{tt.price_eur}</span>
                    </div>
                    <div className="text-white/70 text-xs md:text-sm mt-1.5 md:mt-2">
                      {tt.capacity}
                    </div>
                  </div>

                  {/* CENTER — big elegant price + reserve link */}
                  <div className="hidden md:flex flex-col items-center justify-center px-6 md:px-10 lg:px-14 shrink-0">
                    <div className="flex items-start gap-1.5">
                      <span
                        className="font-display italic font-light leading-none mt-2 md:mt-3"
                        style={{
                          fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
                          color: "#FF6BC6",
                          textShadow: "0 0 3px rgba(255,107,198,0.2)",
                        }}
                      >
                        €
                      </span>
                      <span
                        className="font-heading leading-none tracking-tight"
                        style={{
                          fontSize: "clamp(3.5rem, 6.5vw, 5.5rem)",
                          fontWeight: 900,
                          backgroundImage:
                            "linear-gradient(180deg, #ffffff 0%, #ffe0ef 38%, #ff8ac8 70%, #FF1B8D 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))",
                        }}
                      >
                        {tt.price_eur}
                      </span>
                    </div>
                    <Link
                      to="/book?type=barorder"
                      data-testid={`package-book-${tt.id}`}
                      className="mt-2 md:mt-3 inline-flex items-center gap-1.5 text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-neon hover:text-white transition"
                    >
                      {t("packages.reserve")} <ArrowUpRight size={14} />
                    </Link>
                  </div>

                  {/* RIGHT — actual bottle photo (overflows the row), visible on all screens */}
                  <div className="flex shrink-0 w-24 md:w-32 lg:w-44 xl:w-52 relative items-center justify-center self-stretch">
                    <div
                      className="absolute inset-y-0 left-0 w-px"
                      style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,27,141,0.4) 50%, transparent 100%)" }}
                    />
                    <div
                      className="absolute inset-0 m-auto pointer-events-none"
                      style={{
                        width: "120%",
                        height: "180%",
                        background:
                          "radial-gradient(ellipse at center, rgba(255,27,141,0.4) 0%, rgba(255,27,141,0.18) 40%, transparent 70%)",
                        filter: "blur(14px)",
                      }}
                    />
                    <img loading="lazy" decoding="async"                       src={bottleSrc}
                      alt={`${tt.name} bottle`}
                      data-testid={`package-bottle-${tt.id}`}
                      draggable={false}
                      className="absolute left-1/2 -translate-x-1/2 w-auto select-none pointer-events-none"
                      style={{
                        top: tt.id === "ciroc" ? "32px" : "50%",
                        left: "50%",
                        transform: tt.id === "ciroc" ? "translateX(-50%)" : "translate(-50%, -50%)",
                        height: tt.id === "belvedere" ? "120%" : tt.id === "ciroc" ? "auto" : "108%",
                        maxHeight: tt.id === "belvedere" ? "210px" : tt.id === "ciroc" ? "175px" : "180px",
                        zIndex: 5,
                        filter:
                          "url(#soho-chroma) drop-shadow(0 0 14px rgba(255,27,141,0.7)) drop-shadow(0 0 32px rgba(255,27,141,0.45))",
                      }}
                    />
                  </div>
                </div>

                {/* Mobile CTA bar */}
                <span
                  data-testid={`package-book-mobile-${tt.id}`}
                  className="md:hidden flex items-center justify-between gap-3 px-5 py-3.5 border-t border-neon/30 text-neon font-bold uppercase tracking-[0.28em] text-xs"
                >
                  {t("packages.reserve")} <ArrowUpRight size={16} />
                </span>

                {featured && (
                  <span
                    className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.28em] text-black px-2.5 py-0.5 rounded-full"
                    style={{ background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" }}
                  >
                    ★ {t("packages.featured")}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* BLOCK 2 — Build your own package */}
        <div className="mt-10 md:mt-14 text-center">
          <Link
            to="/book?type=barorder"
            data-testid="build-own-cta"
            className="btn-pink !py-4 !px-8 text-sm tracking-[0.22em] inline-flex"
          >
            <Wine size={18} /> {t("packages.build")}
          </Link>
          <p className="text-white/45 text-xs mt-3 uppercase tracking-widest">{t("packages.buildHint")}</p>
        </div>
      </section>

      {/* ──────────────── BLOCK 3 — WEEKLY LINE-UP ──────────────── */}
      <section className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 py-16 md:py-24">
        <div className="text-center mb-10 md:mb-14">
          <img loading="lazy" decoding="async"             src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/xupotvzy_4B2E6660-F3D6-4EE4-B1E3-F55ED0DF00F0.png"
            alt="Soho69 Weekly Line-Up"
            data-testid="home-weekly-title"
            draggable={false}
            className="block mx-auto w-full max-w-[380px] md:max-w-[560px] h-auto select-none"
            style={{
              filter:
                "url(#soho-chroma) saturate(1.1) contrast(1.05) drop-shadow(0 0 18px rgba(255,27,141,0.55)) drop-shadow(0 0 50px rgba(255,27,141,0.4))",
            }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 md:gap-3">
          {WEEKLY.map((w) => (
            <div
              key={w.day}
              data-testid={`weekly-${w.day.toLowerCase()}`}
              className={
                "relative rounded-3xl overflow-hidden p-5 md:p-4 flex flex-col min-h-[210px] md:min-h-[280px] transition hover:scale-[1.03] " +
                (w.featured ? "ring-glow border border-neon" : "border border-white/10")
              }
              style={{ background: `linear-gradient(160deg, ${w.c1} 0%, ${w.c2} 100%)` }}
            >
              <div className="absolute inset-0 bg-black/55 pointer-events-none" />
              <div className="absolute inset-0 bg-noise opacity-40 mix-blend-overlay pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-white/85">{t(`week.${w.day.toLowerCase()}.day`)}</div>
                <div className="font-heading text-2xl md:text-xl xl:text-[1.4rem] text-white mt-2 leading-tight">
                  {t(w.titleKey)}
                </div>
              </div>
              <div className="relative mt-3 pt-2 flex-1 flex flex-col">
                <div className="text-[11px] md:text-[10.5px] text-white/90 font-medium leading-snug">
                  {t(w.genresKey)}
                </div>
                <div className="text-[11.5px] md:text-[11px] text-white italic font-semibold mt-auto pt-3">
                  &ldquo;{t(w.subKey)}&rdquo;
                </div>
              </div>
              {w.featured && (
                <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest text-black px-2 py-0.5 rounded-full bg-neon">
                  ★
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ──────────────── BLOCK 3.5 — GOOGLE REVIEW · FREE SHOT ──────────────── */}
      <section
        data-testid="home-review-shot"
        className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 py-10 md:py-20"
      >
        <div
          className="relative rounded-4xl overflow-hidden border-2 border-neon p-6 sm:p-8 md:p-12 text-center animate-pulse-slow"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 0%, rgba(255,27,141,0.42) 0%, rgba(31,0,44,0.92) 55%, #0a0014 100%)",
            boxShadow:
              "0 0 60px rgba(255,27,141,0.55), 0 0 120px rgba(255,27,141,0.25), inset 0 0 60px rgba(255,27,141,0.18)",
          }}
        >
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-neon font-bold">
            Show &amp; Save
          </div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.95]">
            <span aria-hidden className="inline-block mr-1">🥃</span>
            Free <span className="text-neon">Shot</span> On The House
          </h2>
          <p className="mt-4 text-white/85 text-sm sm:text-base md:text-base max-w-2xl mx-auto leading-relaxed">
            Left us a <span className="text-neon font-bold">5-star Google review</span>?
            Show it to our bar staff and claim your free shot.
            <br className="hidden sm:block" /> One per guest, per visit.
          </p>
          <div className="mt-7 flex flex-col items-stretch sm:flex-row sm:items-center sm:justify-center gap-3">
            <a
              href="https://g.page/r/CexRaQAWMSg4EAE/review"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="review-cta"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 rounded-full font-bold text-black uppercase tracking-[0.22em] text-sm md:text-base transition-transform active:scale-[0.98]"
              style={{
                background:
                  "linear-gradient(135deg,#FF1B8D 0%,#FF6BC6 50%,#FF1B8D 100%)",
                boxShadow:
                  "0 0 28px rgba(255,27,141,0.7), 0 0 60px rgba(255,27,141,0.4), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
            >
              ★★★★★ Leave a Google Review
            </a>
            <a
              href="https://g.page/r/CexRaQAWMSg4EAE/review"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="claim-shot-cta"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 rounded-full font-bold uppercase tracking-[0.22em] text-sm md:text-base text-white transition-transform active:scale-[0.98]"
              style={{
                background: "rgba(0,0,0,0.55)",
                border: "2px solid #FF1B8D",
                boxShadow:
                  "0 0 22px rgba(255,27,141,0.35), inset 0 0 22px rgba(255,27,141,0.15)",
              }}
            >
              🥃 Claim Free Shot
            </a>
          </div>
          <p className="mt-5 text-[10px] sm:text-xs uppercase tracking-[0.28em] text-white/55">
            Show the review at the bar — free shot on us.
          </p>
        </div>
      </section>

      {/* ──────────────── BLOCK 4 — HOW TO FIND US ──────────────── */}
      <section className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 py-16 md:py-24">
        <div className="text-center mb-10 md:mb-14">
          <img loading="lazy" decoding="async"             src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/71udcrit_4F172C9A-1DA3-4CA2-9C24-430A8F89350F.png"
            alt="Find the door"
            data-testid="home-find-title"
            draggable={false}
            className="block mx-auto w-full max-w-[380px] md:max-w-[560px] h-auto select-none"
            style={{
              filter:
                "url(#soho-chroma) saturate(1.1) contrast(1.05) drop-shadow(0 0 18px rgba(255,27,141,0.55)) drop-shadow(0 0 50px rgba(255,27,141,0.4))",
            }}
          />
          <p className="text-white/65 mt-4 max-w-xl mx-auto text-sm md:text-base">{t("find.sub")}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 md:gap-8 items-stretch">
          {/* Map */}
          <div className="rounded-4xl overflow-hidden border border-neon/30 ring-glow min-h-[360px] lg:min-h-[520px]">
            <iframe
              title="Soho69 Tenerife"
              data-testid="home-map"
              src="https://www.google.com/maps?q=Soho69+Tenerife&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          {/* Address + entrance details */}
          <div className="flex flex-col gap-4 md:gap-5">
            <div className="surface rounded-4xl p-6 md:p-7 flex gap-4 items-start">
              <MapPin size={28} className="text-neon shrink-0 mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{t("find.addressLabel")}</div>
                <div className="font-heading text-3xl mt-2">C.C. Veronicas 3</div>
                <div className="text-white/70 mt-1">Playa de las Américas · Tenerife · {t("find.country")}</div>
                <a
                  href="https://maps.google.com/?q=Soho69+Tenerife"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="find-directions"
                  className="btn-pink mt-5 !py-3 !px-5 text-xs"
                >
                  <ChevronRight size={14} /> {t("find.directions")}
                </a>
              </div>
            </div>

            <div className="surface rounded-4xl p-6 md:p-7">
              <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{t("find.entranceLabel")}</div>
              <p className="text-white/75 mt-2 text-sm md:text-base">{t("find.entranceText")}</p>
              <div className="mt-5 grid grid-cols-3 gap-2">
                {[
                  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/fe3qeztr_A8BE79AF-CE10-4EC3-8805-72DE08E81B2A.png",
                  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/b875fxv2_33F7EF25-1189-4172-A821-C7F8CC39C164.png",
                  "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/n0gutrdx_25FD0262-CF45-4B26-8A6B-B1F9F47D12C7.png",
                ].map((src, i) => (
                  <a
                    key={src}
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`entrance-photo-${i}`}
                    className="group aspect-square rounded-2xl overflow-hidden border border-white/10 hover:border-neon transition block"
                  >
                    <img
                      src={src}
                      alt={`Soho69 entrance ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
